import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/base/SEOSchema";
import { categories, categoryImages } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

const categoryNames: Record<string, string> = {
  hot: "热门推荐",
  movie: "最新电影",
  tv: "电视剧",
  variety: "综艺节目",
  anime: "动漫动画",
  doc: "纪录片",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || "hot");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlug]);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const categoryArticles = articles.filter((a) => a.category === activeSlug);
  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = categoryNames[activeSlug] || currentCategory?.name || activeSlug;

  return (
    <>
      <SEOSchema
        type="WebPage"
        data={{
          name: `${categoryName} - 91影视看片下载`,
          description: `91影视看片下载${categoryName}专区，海量${categoryName}资源免费在线观看和下载。`,
          url: `https://www.91yingshi.com/category/${activeSlug}`,
          isPartOf: {
            "@type": "WebSite",
            name: "91影视看片下载",
            url: "https://www.91yingshi.com",
          },
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.91yingshi.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: categoryName,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb items={[{ label: categoryName }]} />

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-lg">
                  <i className={`${currentCategory?.icon || "ri-fire-line"} text-background-50 text-xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 {categoryArticles.length} 篇内容
                  </p>
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeSlug === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Article List */}
          <section className="w-full py-10 md:py-14">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer border border-background-200 hover:border-primary-200 transition-all"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        {article.category === "hot" && (
                          <div className="absolute top-3 left-3">
                            <span className="bg-primary-500 text-background-50 text-xs px-2.5 py-1 rounded-full font-medium">
                              热门
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-500 line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views > 10000
                              ? `${(article.views / 10000).toFixed(1)}万`
                              : article.views}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="bg-secondary-100 text-secondary-700 text-xs px-2 py-0.5 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">{t("no_content")}</p>
                </div>
              )}
            </div>
          </section>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}